import { useState, useEffect, useCallback } from 'react'
import { useTheme } from 'theme/useTheme';

export type ScreenLayout = 'mobile' | 'tablet' | 'desktop'

export default function useWindowSize() {
    const isClient = typeof window === 'object'
    const theme = useTheme()

    const getSize = useCallback(() => {
        const getDeviceLayout = (screenWidth: number): ScreenLayout => {
            if (screenWidth < theme.breakpoints.values.tablet) {
                return 'mobile'
            } else if (screenWidth < theme.breakpoints.values.desktop) {
                return 'tablet'
            } else {
                return 'desktop'
            }
        }

        return {
            width: isClient ? window.innerWidth : 800,
            height: isClient ? window.innerHeight : 600,
            screenLayout: getDeviceLayout(window.innerWidth),
            touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0
        }
    }, [isClient, theme.breakpoints.values.desktop, theme.breakpoints.values.tablet])

    const [windowSize, setWindowSize] = useState(getSize)

    useEffect(() => {
        if (!isClient) {
            return
        }

        function handleResize() {
            setWindowSize(getSize())
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [isClient, getSize]) // Empty array ensures that effect is only run on mount and unmount

    return windowSize
}

export function useScreenLayout() {
    const isClient = typeof window === 'object'
    const theme = useTheme()

    const getSize = useCallback(() => {
        const getDeviceLayout = (screenWidth: number): ScreenLayout => {
            if (screenWidth < theme.breakpoints.values.tablet) {
                return 'mobile'
            } else if (screenWidth < theme.breakpoints.values.desktop) {
                return 'tablet'
            } else {
                return 'desktop'
            }
        }

        return getDeviceLayout(window.innerWidth)

    }, [theme.breakpoints.values.desktop, theme.breakpoints.values.tablet])

    const [screenLayout, setScreenLayout] = useState(getSize)

    useEffect(() => {
        if (!isClient) {
            return
        }

        function handleResize() {
            setScreenLayout(getSize())
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [isClient, getSize]) // Empty array ensures that effect is only run on mount and unmount

    return screenLayout
}
